import { action, computed, observable } from 'mobx';

import Model from 'core/model/Model';
import api from 'core/util/api';
import { getIPSortValue } from 'core/util/sortUtils';
import $auth from 'app/stores/$auth';
import $devices from 'app/stores/device/$devices';
import $kentikAgents from 'app/stores/kentikAgent/$kentikAgents';
import $setup from 'app/stores/$setup';
import $sites from 'app/stores/site/$sites';
import $labels from 'app/stores/label/$labels';
import $metrics from 'app/stores/metrics/$metrics';
import { ApiBatcher } from 'app/stores/util/apiBatcher';
import { showSuccessToast } from 'core/components';
import MetricDeviceModel from 'app/stores/metrics/MetricDeviceModel';
import { DEVICE_CPU_QUERY_SERIES } from 'app/views/metrics/queries';
import { DEFAULT_DEVICE_CLASS } from '@kentik/ui-shared/nms/deviceMetadata';
import { GROUP_ID_PREFIX } from '@kentik/ui-shared/agents/constants';
import { AllDeviceClass, DeviceClassToCapabilities } from 'app/util/nms/deviceMetadata';
import { hasRequiredLabel, requiredDeviceLabels } from 'app/stores/auth/AuthRbacLabels';
import { DeviceSyslogsStatus } from 'app/stores/device/DeviceSyslogsStatus';
import { DeviceSyslogsCollection } from 'app/stores/device/DeviceSyslogsCollection';

const deviceTypeAcronyms = {
  router: 'Router',
  host: 'cHst',
  'host-nprobe-basic': 'nHst',
  'host-nprobe-dns-www': 'KPb'
};

const fpsFetch = new ApiBatcher({ url: '/api/ui/devices/batch/fps', batchWindow: 100 });
const flowFetch = new ApiBatcher({ url: '/api/ui/devices/batch/flow', batchWindow: 100 });

class DeviceModel extends Model {
  /**
   * @type {import('app/stores/metrics/MetricDeviceModel').default | null}
   */
  @observable.ref
  metricsDevice = null;

  @observable.ref
  connections = null;

  @observable
  hasFetched = false;

  @observable
  fps_data;

  @observable
  _fpsLoading = true;

  @observable
  _flowLoading = true;

  @observable
  _hasFlow = false;

  _telemetryLoading;

  _componentsLoading;

  _isisAdjacenciesLoading;

  _bgpNeighborsLoading;

  @observable
  fetchedTelemetry = false;

  @computed
  get syslogs() {
    return new DeviceSyslogsCollection(this);
  }

  @computed
  get loadingSyslogStatus() {
    return !!DeviceSyslogsStatus.fetchPromise && !DeviceSyslogsStatus.fetched;
  }

  // Note that you need to manually trigger `getDeviceSyslogsStatus` for this to work
  @computed
  get hasSyslogs() {
    if (DeviceSyslogsStatus.fetched) {
      return DeviceSyslogsStatus.lastFetched[this.id] || null;
    }
    if (!DeviceSyslogsStatus?.fetchPromise) {
      console.warn('Attempted to display syslogs status for device prior to triggering API fetch!');
    }
    return null;
  }

  @action
  async fetchSyslogsStatus() {
    return DeviceSyslogsStatus.getDeviceSyslogsStatus();
  }

  /**
   * when checking whether fps is currently loading, you probably want to ensure that it _is_ loading.
   * note that fpsFetch debounces these requests cleanly so it won't spam the API.
   *
   * prefer using `.hasFlow()` for most simple flow checks
   * @returns {boolean}
   */
  @computed
  get fpsLoading() {
    // we can use existing fps_data or raw_fps if it was delivered alongside the original device data
    // otherwise we fetch from batch FPS API
    const isLoading = !this.fps_data && this.get('raw_fps') == null && this._fpsLoading;
    if (isLoading) {
      this._fetchFps();
    }
    return isLoading;
  }

  @computed
  get canUpdate() {
    return (
      $auth.hasRbacPermissions(['device::update']) && hasRequiredLabel(requiredDeviceLabels.get().update, this.labels)
    );
  }

  @computed
  get canDelete() {
    return (
      $auth.hasRbacPermissions(['device::delete']) && hasRequiredLabel(requiredDeviceLabels.get().delete, this.labels)
    );
  }

  /**
   * You shouldn't have to call this directly.
   *
   * @returns {Promise<boolean>}
   * @private
   */
  @action
  async _fetchFlow() {
    const result = await flowFetch.fetch(Number.parseInt(this.id));
    this._flowLoading = false;
    this._hasFlow = !!result;
    return this._hasFlow;
  }

  @action
  async loadDeviceStatus() {
    return api.get(`/api/ui/devices/status/${this.id}`).then((response) => {
      const status = response[this.id];

      if (status) {
        this.set(status);
      }

      return status;
    });
  }

  /**
   * Loads the config status for this device if configs are enabled.
   * Sets the configStatus attribute with revision info, fetch status, etc.
   * @returns {Promise<void>}
   */
  @action
  async loadConfigStatus() {
    if (!this.hasConfigs) {
      return Promise.resolve();
    }

    try {
      const response = await api.post('/api/ui/devices/configs?includeData=false', {
        data: { deviceIds: [this.id] }
      });

      const deviceConfig = response[this.id];
      if (deviceConfig) {
        const configData = {
          revisionId: deviceConfig.revision?.id,
          firstFetched: deviceConfig.revision?.first_fetched,
          lastFetched: deviceConfig.revision?.last_fetched,
          configSize: deviceConfig.config_data?.length || undefined,
          fetchError: deviceConfig.fetch_error ? deviceConfig.config_data : false
        };
        this.set('configStatus', configData);
        return configData;
      }
      return null;
    } catch (error) {
      console.error(`Failed to fetch config status for device ${this.id}:`, error);
      this.set('configStatus', { fetchError: true });
      throw error;
    }
  }

  @computed
  get hasFlow() {
    if (this._flowLoading && this._fpsLoading) {
      // trigger flow fetch if necessary (we can use FPS data here if it's already loaded!)
      this._fetchFlow();
    }
    if (!this._flowLoading) {
      // expected response
      return this._hasFlow;
    }
    if (!this._fpsLoading) {
      // we can make use of fps data if already loaded, but we don't want to trigger a fetch unnecessarily
      return this.rawFps > 0 || null;
    }
    // null value means that it's still loading, you can check for this condition or just treat it as falsy
    return null;
  }

  @computed
  get urlRoot() {
    return '/api/ui/devices';
  }

  get defaults() {
    return {
      device_snmp_v3_conf_enabled: false,
      sending_ips: [''],
      cdn_attr: 'N',
      labels: [],
      bgpPeerIP4: null,
      bgpPeerIP6: null,
      minimize_snmp: false,
      bgp_enabled: true,
      device_bgp_type: 'device',
      device_gnmi_v1_conf: null
    };
  }

  get omitDuringSerialize() {
    return ['all_interfaces', 'metricsDevice', 'plan', 'fps5m'];
  }

  @computed
  get percentClassified() {
    if (!this.get('interface_classification')) {
      return 0;
    }
    const classified = this.get('interface_classification.interfacesClassifiedCount') ?? 0;
    const total = this.get('interface_classification.interfaceCount') ?? 0;
    return total === 0 ? 0 : Math.round((classified / total) * 100);
  }

  /**
   * Fetch FPS data from KDE.
   * @private
   * @returns {Promise<void>}
   */
  @action
  async _fetchFps() {
    const result = await fpsFetch.fetch(Number.parseInt(this.id));
    this._fpsLoading = false;
    if (result) {
      this.fps_data = result;
    } else {
      // zero out flow for device which has no recorded flow data
      this.fps_data = { fps: 0, downsampled: false, downsampled_fps: 0 };
    }
  }

  @action
  fetchTelemetry() {
    if (!this._telemetryLoading) {
      this._telemetryLoading = api
        .get(`/api/ui/devices/telemetry/${this.id}`)
        .then((telemetry) => {
          this.fetchedTelemetry = true;
          this.metricsDevice?.set(telemetry);
        })
        .catch((error) => {
          // Reset loading flag on error so callers can retry
          this._telemetryLoading = undefined;
          // Optional: surface error for upstream handling
          throw error;
        });
    }

    return this._telemetryLoading;
  }

  @action
  fetchComponents() {
    if (!this._componentsLoading) {
      this._componentsLoading = api
        .get(`/api/ui/devices/components/${this.id}`)
        .then((components) => {
          this.metricsDevice?.loadComponents(components);
        })
        .catch((error) => {
          // Reset loading flag on error so callers can retry
          this._componentsLoading = undefined;
          throw error;
        });
    }

    return this._componentsLoading;
  }

  @action
  fetchIsisAdjacencies() {
    if (!this._isisAdjacenciesLoading) {
      this._isisAdjacenciesLoading = api
        .get(`/api/ui/devices/isis-adjacencies/${this.id}`)
        .then((isisAdjacencies) => {
          this.metricsDevice?.loadIsisAdjacencies(isisAdjacencies);
        })
        .catch((error) => {
          // Reset loading flag on error so callers can retry
          this._isisAdjacenciesLoading = undefined;
          throw error;
        });
    }

    return this._isisAdjacenciesLoading;
  }

  @action
  fetchBgpNeighbors() {
    if (!this._bgpNeighborsLoading) {
      this._bgpNeighborsLoading = api
        .get(`/api/ui/devices/bgp-neighbors/${this.id}`)
        .then((neighbors) => {
          this.metricsDevice?.loadNeighbors(neighbors);
        })
        .catch((error) => {
          // Reset loading flag on error so callers can retry
          this._bgpNeighborsLoading = undefined;
          throw error;
        });
    }

    return this._bgpNeighborsLoading;
  }

  async savePartial() {
    return this.save(
      { device_status: this.get('device_status') || 'INI' },
      { url: '/api/ui/devices/partial', toast: false }
    );
  }

  saveComplete() {
    const url = this.isNew && this.get('device_status') !== 'INI' ? '/api/ui/devices/' : '/api/ui/devices/complete';
    return this.save({}, { url, toast: false });
  }

  serialize(data) {
    const site_id = data.site?.id ?? data.site_id;
    const plan_id = data.plan?.id ?? data.plan_id;

    if (!data.snmp_collection_method?.includes('kproxy')) {
      data.device_snmp_ip = null;
      data.device_snmp_community = '';
      data.device_snmp_v3_conf_enabled = false;
    }

    data.device_gnmi_v1_conf = data.streaming_telemetry_collection_method?.includes('kproxy')
      ? { ...data.device_gnmi_v1_conf, dialoutServer: 'auto' }
      : null;

    // we want to store ST timeout as Go duration, ie "20s"
    if (data.streaming_telemetry_collection_options?.timeout) {
      data.streaming_telemetry_collection_options.timeout = `${data.streaming_telemetry_collection_options.timeout}s`;
    }

    const device_snmp_v3_conf =
      data.device_snmp_v3_conf_enabled && data.device_snmp_v3_conf.UserName.trim() ? data.device_snmp_v3_conf : null;

    if (data.bgp_enabled === false) {
      data.device_bgp_type = 'none';
    } else if (data.bgp_enabled === true) {
      if (data.device_bgp_type === 'other_device') {
        delete data.device_bgp_neighbor_ip;
        delete data.device_bgp_neighbor_ip6;
      }
      if (data.device_bgp_neighbor_ip || data.device_bgp_neighbor_ip6) {
        data.device_bgp_type = 'device';
      } else if (data.use_bgp_device_id) {
        data.device_bgp_type = 'other_device';
      }
    }

    if (data.device_bgp_type === 'device' || data.device_bgp_type === 'none') {
      data.use_bgp_device_id = null;
    }

    if ($devices.getDeviceTypeFromSubtype(data.device_subtype) !== 'host-nprobe-dns-www') {
      delete data.cdn_attr;
    }

    if (data.device_bgp_type !== 'device') {
      data.device_bgp_neighbor_ip = null;
      data.device_bgp_neighbor_ip6 = null;
      data.device_bgp_neighbor_asn = null;
      data.device_bgp_password = null;
      data.device_bgp_flowspec = false;
      data.device_bgp_label_unicast = false;
      data.bgpPeerIP4 = null;
      data.bgpPeerIP6 = null;
    }

    if (data.device_bgp_type !== 'none') {
      data.use_asn_from_flow = false;
    }

    if (data.interfaceCollection) {
      delete data.interfaceCollection;
    }

    return super.serialize({ ...data, site_id, plan_id, device_snmp_v3_conf });
  }

  deserialize(data) {
    if (data && data[this.jsonRoot]) {
      data = super.deserialize(data);
    }

    if (data) {
      data.bgp_enabled = data.device_bgp_type !== 'none';

      if (data.device_bgp_neighbor_ip && data.device_bgp_neighbor_ip6) {
        data.bgp_ip_type = 'both';
      } else if (data.device_bgp_neighbor_ip6) {
        data.bgp_ip_type = 'ipv6';
      } else {
        data.bgp_ip_type = 'ipv4';
      }
    }

    // remove the "s" from the end of the durations
    if (data.streaming_telemetry_collection_options) {
      const { timeout } = data.streaming_telemetry_collection_options;
      data.streaming_telemetry_collection_options.timeout = timeout?.replace(/s$/, '');
    }

    if (data.metricsDevice?.id) {
      this.metricsDevice ??= new MetricDeviceModel();
      this.metricsDevice.set(this.metricsDevice.deserialize(data.metricsDevice));
      this.metricsDevice.set({
        componentCount: data.component_count,
        neighborCount: data.neighbor_count,
        adjacencyCount: data.adjacency_count,
        neighborNotEstablishedCount: data.neighbor_nonestablished_count
      });
    }

    return data;
  }

  @action
  async save(attributes = {}, options = {}) {
    const { isNew } = this;

    return super.save(attributes, options).then((result) => {
      $setup.getPlans({ force: true });

      if (this.get('device_status') !== 'INI') {
        $devices.loadDeviceSummaries();
      }

      if (isNew && this.isNMS) {
        $metrics.deviceCollection.fetch({ force: true });
      }

      return result;
    });
  }

  @action
  async unarchive(options = {}) {
    const { toast = true, url = `${this.url}/unarchive` } = options;

    this.requestStatus = 'updating';

    return api.put(url).then(
      () => {
        this.set('device_status', 'V');
        this.requestStatus = null;

        if (toast) {
          showSuccessToast(`Device ${this.get('device_name')} was restored successfully`);
        }

        return true;
      },
      (error) => {
        this.error = { label: 'updating' };
        this.requestStatus = null;
        throw error;
      }
    );
  }

  @action
  async fetch(options = {}) {
    if (!this.hasFetched || options.force) {
      return super.fetch(options).then(() => {
        this.hasFetched = true;
      });
    }
    return Promise.resolve();
  }

  @action
  async destroy(options) {
    const { isActive } = this;

    return super.destroy({ remove: !isActive, ...options }).then((result) =>
      $devices.loadDeviceSummaries().then(() => {
        if (isActive) {
          this.set('device_status', 'D');
        } else {
          // force sites to refresh to account for an associated device being removed
          $sites.collection.resetFetchCache();
        }

        return result;
      })
    );
  }

  get sortValues() {
    return {
      id: () => parseInt(this.get('id')),
      sending_ips: () =>
        this.get('sending_ips') && this.get('sending_ips').length && getIPSortValue(this.get('sending_ips')[0]),
      deviceType: () => this.deviceType.toLowerCase(),
      site_name: () => (this.get('site_name') ? this.get('site_name').toLowerCase() : 'zzzzzz'),
      'stats.RibPrefixes': () => this.get('stats.RibPrefixes') || 0 + this.get('stats.RibPrefixes6') || 0,
      configs: () => {
        if (!this.hasConfigFetch) {
          return 'zzz'; // Devices without configs sort last
        }
        const configStatus = this.get('configStatus');
        if (!configStatus) {
          return 'b'; // Warning: no data
        }
        if (configStatus.fetchError) {
          return 'a'; // Error: sorts first
        }
        // Success: sort by last fetched time (most recent first, use negative timestamp)
        const lastFetched = configStatus.lastFetched?.seconds || 0;
        return `c${String(9999999999 - lastFetched).padStart(10, '0')}`;
      }
    };
  }

  get messages() {
    return {
      create: `Device ${this.get('device_name')} was added successfully`,
      update: `Device ${this.get('device_name')} was updated successfully`,
      destroy: `Device ${this.get('device_name')} was removed successfully`
    };
  }

  get displayedMonitoringTemplateId() {
    // If device is an NMS device AND it does not have a monitoring template ID set, we want to show the fallback "Everything" default template.
    // The Everything template is functionally the same as having no monitoring template set,
    // and this matches what we show in the device configuration modal for these devices.
    const defaultMonitoringTemplate = this.isNMS
      ? $metrics.monitoringTemplatesCollection.defaultTemplate?.id
      : undefined;
    return this.get('monitoring_template_id') || defaultMonitoringTemplate;
  }

  /**
   * Returns us the number of Interfaces that were Classified / Matched by a particular Rule. Need this at the
   * device level to show breakdowns in the Rule Details Dialog when testing Rules against Devices.
   */
  interfacesMatchedByRule = (rule) => {
    const rules = this.get('rules');

    if (rule && rules) {
      return rules.find((r) => r.id === rule.id);
    }

    return null;
  };

  /**
   * @returns {number[]}
   */
  @computed
  get labels() {
    if (this.has('labels')) {
      return $labels.getLabelsById(this.get('labels').map((label) => label.id));
    }
    return $labels.getLabels('device', this.id);
  }

  // we use this field to allow searching by label
  @computed
  get appliedLabels() {
    return this.labels.length > 0 ? this.labels.map((label) => label.get('name')).join(' ') : 'no labels';
  }

  @computed
  get hasVendor() {
    return !!this.get('device_vendor_type') || !!this.get('vendor');
  }

  @computed
  get vendor() {
    return this.get('device_vendor_type') || this.get('vendor') || this.get('device_model_type') || this.get('model');
  }

  @computed
  get temperature_last() {
    return this.get('temperature')?.last_instant;
  }

  @computed
  get isUnobservable() {
    return this.get('nms_status') === 'unobservable';
  }

  @computed
  get statusDisplay() {
    const status = this.get('nms_status');
    if (!status) {
      return 'Unknown';
    }

    return status.charAt(0).toUpperCase() + status.slice(1);
  }

  @computed
  get statusIntent() {
    const status = this.get('nms_status');

    if (!status) {
      return 'none';
    }

    const statusIntent = {
      down: 'danger',
      up: 'success',
      unknown: 'none',
      unobservable: 'warning',
      possibly_down: 'warning'
    };

    return statusIntent[status];
  }

  @computed
  get statusRaw() {
    const { bgpStatus, downSampledFps } = this;
    const flowStatusRaw = downSampledFps > 0 ? 0 : 1;

    const snmpStatusRaw = this.get('snmp') ? 0 : 1;

    let bgpStatusRaw;
    if (bgpStatus.v4.established || bgpStatus.v6.established) {
      bgpStatusRaw = 0;
    } else if (!bgpStatus.v4.configured && !bgpStatus.v6.configured) {
      bgpStatusRaw = 1;
    } else {
      bgpStatusRaw = 2;
    }

    return flowStatusRaw + snmpStatusRaw + bgpStatusRaw;
  }

  /**
   * Note that this can be somewhat expensive on KDE. The advantage is that it gives a result which is extremely recent (unless local caching gets in the way)
   * @returns {number|null}
   */
  @computed
  get rawFps() {
    if (!this.fps_data && this.get('raw_fps') == null) {
      this._fetchFps();
      return null;
    }

    let fps = 0;
    if (this.get('raw_fps') != null) {
      // legacy method - just grab the raw_fps from the server
      fps = parseFloat(this.get('raw_fps'));
    } else if (this.fps_data) {
      if (parseInt(this.fps_data.avg_device_sample_rate) === 0 && this.get('metadata')) {
        fps = Math.ceil((this.fps_data.avg_sample_rate / this.get('metadata').device_sample_rate) * this.fps_data.fps);
      } else {
        fps = Math.ceil((this.fps_data.avg_sample_rate / this.fps_data.avg_device_sample_rate) * this.fps_data.fps);
      }
    }

    return Number.isNaN(fps) ? 0 : fps;
  }

  /**
   * Note that this can be somewhat expensive on KDE. The advantage is that it gives a result which is extremely recent (unless local caching gets in the way)
   * @returns {number|null}
   */
  @computed
  get downSampledFps() {
    if (!this.fps_data && this.get('downsampled_fps') == null) {
      this._fetchFps();
      return null;
    }

    let fps = 0;
    if (this.get('downsampled_fps') != null) {
      fps = parseFloat(this.get('downsampled_fps'));
    } else if (this.fps_data) {
      fps = Math.ceil(this.fps_data.fps);
    }

    return Number.isNaN(fps) ? 0 : fps;
  }

  @computed
  get flowType() {
    return this.get('stats.FlowType');
  }

  @computed
  get isActive() {
    return this.get('device_status') === 'V';
  }

  @computed
  get isArchived() {
    return this.get('device_status') === 'D';
  }

  @computed
  get isIncomplete() {
    return this.get('device_status') === 'INI';
  }

  @computed
  get flowSource() {
    const isAgent = parseInt(this.get('stats.IsAgent'));

    if (!this.rawFps) {
      return 'none';
    }
    // 0, NaN, null etc
    if (!isAgent) {
      return 'client';
    }
    if (isAgent === 1) {
      return 'agent';
    }
    return 'none';
  }

  @computed
  get bgpConfigured() {
    const { v4, v6 } = this.bgpStatus;

    return v4.configured || v6.configured;
  }

  @computed
  get hasLabels() {
    return this.get('labels') && this.get('labels').length > 0;
  }

  @computed
  get deviceType() {
    return this.get('device_subtype') || deviceTypeAcronyms[this.get('device_type')];
  }

  @computed
  get sendingIpsDisplay() {
    return this.get('sending_ips') && this.get('sending_ips').join(', ');
  }

  getDefaultSampleRateForDeviceType = (device_type) => (device_type === 'router' ? 1024 : 32);

  get snmpVersion() {
    if (!this.get('snmp_enabled')) {
      return false;
    }

    if (this.get('device_snmp_v3_conf_enabled') && this.get('device_snmp_v3_conf.UserName')) {
      return 'V3';
    }

    return 'V2';
  }

  // snmp_enabled is a derived field (set in augmentDeviceWithBasicMetadata by getDeviceStatus.js)
  // which indicates whether the device has been configured for
  // - Flow Enrichment via kProxy
  // - Flow Enrichment via Universal Agent
  // - NMS (Kentik Agent)
  @computed
  get snmpStatus() {
    return this.get('snmp_enabled') && this.get('snmp_healthy');
  }

  @computed
  get stStatus() {
    return this.stEnabled && this.get('st_healthy');
  }

  @computed
  get flowConfigured() {
    return !!this.get('sending_ips').length && !!this.get('device_sample_rate');
  }

  @computed
  get nmsConfigured() {
    return this.isNMS || this.isICMP;
  }

  @computed
  get snmpEnrichmentStatus() {
    if (this.isICMPOnly) {
      return 'unknown';
    }
    return this.snmpStatus;
  }

  @computed
  get stEnrichmentStatus() {
    if (!this.stEnabled || this.isICMPOnly) {
      return 'unknown';
    }
    return this.get('st_healthy') ? 'up' : 'down';
  }

  @computed
  get snmpNmsStatus() {
    if (!this.isNMS || this.isICMPOnly) {
      return 'unknown';
    }
    return this.get('nms_status');
  }

  @computed
  get stNmsStatus() {
    if (!this.isNMS || this.isICMPOnly) {
      return 'unknown';
    }
    return this.hasStreamingTelemetry ? 'up' : 'down';
  }

  @computed
  get icmpStatus() {
    if (!this.isICMP) {
      return 'unknown';
    }
    return this.get('nms_status');
  }

  @computed
  get bgpStatus() {
    const status = {
      v4: { established: false, configured: false },
      v6: { established: false, configured: false }
    };

    if (this.get('device_bgp_type') === 'device') {
      status.v4 = {
        established: parseInt(this.get('stats.RibSessionState')) === 1,
        configured: !!this.get('metadata.device_bgp_neighbor_ip')
      };
      status.v6 = {
        established: parseInt(this.get('stats.RibSessionState6')) === 1,
        configured: !!this.get('metadata.device_bgp_neighbor_ip6')
      };
    } else if (this.get('device_bgp_type') === 'other_device') {
      status.v4 = { established: true, configured: true };
      status.v6 = { established: true, configured: true };
    }

    return status;
  }

  @computed
  get bgpCombinedStatus() {
    const { v4, v6 } = this.bgpStatus;
    let status = 'not configured';

    if (v4.configured || v6.configured) {
      if (v4.established || v6.established) {
        status = 'established';
      } else {
        status = 'not established';
      }
    }

    return status;
  }

  @computed
  get bgpUpdates() {
    return {
      v4: parseInt(this.get('stats.RibUpdates')) || 0,
      v6: parseInt(this.get('stats.RibUpdates6')) || 0
    };
  }

  @computed
  get stEnabled() {
    const st = this.get('device_gnmi_v1_conf') || false;
    return st && st.dialoutServer === 'auto';
  }

  deviceFilter(filterField = 'i_device_id') {
    return {
      connector: 'All',
      filterGroups: [
        {
          connector: 'All',
          not: false,
          filters: [
            {
              filterField,
              operator: '=',
              filterValue: `${this.id}`
            }
          ]
        }
      ]
    };
  }

  @computed
  get isNMS() {
    return this.get('snmp_collection_method')?.includes('kentik_agent');
  }

  @computed
  get isEnrichingFlowWithKproxy() {
    return this.get('snmp_collection_method')?.includes('kproxy');
  }

  @computed
  get isEnrichingFlowWithUA() {
    return this.get('snmp_collection_method')?.includes('kentik_agent_flow_only');
  }

  @computed
  get isICMP() {
    return !!this.get('ranger_icmp_ip');
  }

  @computed
  get isICMPOnly() {
    return this.isICMP && !this.flowConfigured;
  }

  @computed
  get hasStreamingTelemetry() {
    return this.get('streaming_telemetry_collection_method')?.includes('kentik_agent');
  }

  @computed
  get hasKentikAgent() {
    return !!this.get('device_kentik_agent_id');
  }

  @computed
  get hasCommandAccess() {
    const hasPermission = $auth.hasPermission('recon.deviceConfigFetch.enabled', { overrideForSudo: false });
    return (
      hasPermission &&
      (!!this.get('deviceconf_options.enabled') ||
        !!this.get('deviceconf_enabled') ||
        // Fall back to legacy fields if the first-class "enabled" flag is not set
        !!this.get('deviceconf_options.agent_id') ||
        !!this.get('deviceconf_agent_id'))
    );
  }

  @computed
  get hasConfigFetch() {
    return (
      this.hasCommandAccess &&
      (!!this.get('deviceconf_options.fetch_enabled') ||
        !!this.get('deviceconf_fetch_enabled') ||
        // Fall back to legacy fields if the first-class "fetch_enabled" flag is not set
        !!this.get('deviceconf_options.fetch_interval') ||
        !!this.get('deviceconf_fetch_interval'))
    );
  }

  /**
   * Names of the credentials in use by the device
   */
  @computed
  get credentials() {
    const credentials = [];

    if (this.get('device_status') === 'V') {
      if (this.isNMS) {
        const name = this.get('kentik_agent_snmp_options.credential_name') || this.get('agent_snmp_credential_name');
        if (name) {
          credentials.push(name);
        }
      }

      if (this.hasStreamingTelemetry) {
        const name =
          this.get('streaming_telemetry_collection_options.credential_name') || this.get('agent_st_credential_name');
        if (name) {
          credentials.push(name);
        }
      }

      if (this.hasCommandAccess) {
        const configCred = this.get('deviceconf_options.credential_name') || this.get('deviceconf_credential_name');
        if (configCred) {
          credentials.push(configCred);
        }
      }
    }

    return credentials;
  }

  @computed
  get agentName() {
    const agentId = this.get('device_kentik_agent_id');

    // check loading to force a recompute after it finishes
    if (agentId && !$kentikAgents.collection.loading) {
      if (agentId.startsWith(GROUP_ID_PREFIX)) {
        return $kentikAgents.getNmsAssignmentOption(agentId)?.label ?? agentId;
      }
      return $kentikAgents.collection.get(agentId)?.name ?? agentId;
    }

    return null;
  }

  @computed
  get cpuUtilizationQuery() {
    return {
      ...DEVICE_CPU_QUERY_SERIES,
      kmetrics: {
        ...DEVICE_CPU_QUERY_SERIES.kmetrics,
        filters: this.deviceFilter()
      }
    };
  }

  @computed
  get memoryUtilizationQuery() {
    return {
      use_kmetrics: true,
      show_overlay: false,
      show_total_overlay: false,
      kmetrics: {
        measurement: '/system/memory',
        dimensions: ['device_name'],
        metrics: [
          {
            name: 'utilization',
            type: 'gauge'
          }
        ],
        merge: {
          aggregate: 'avg',
          dimensions: ['index']
        },
        range: {
          lookback: 'PT86400S'
        },
        window: {
          size: 0,
          fn: {
            utilization: 'avg'
          }
        },
        rollups: {
          avg_utilization: {
            metric: 'utilization',
            aggregate: 'avg'
          },
          max_utilization: {
            metric: 'utilization',
            aggregate: 'max'
          },
          last_utilization: {
            metric: 'utilization',
            aggregate: 'last'
          }
        },
        sort: [
          {
            name: 'avg_utilization',
            direction: 'desc'
          },
          {
            name: 'max_utilization',
            direction: 'desc'
          },
          {
            name: 'last_utilization',
            direction: 'desc'
          }
        ],
        limit: 5,
        includeTimeseries: 5,
        viz: {
          type: 'area',
          rollup: '',
          metric: 'utilization'
        },
        filters: this.deviceFilter()
      }
    };
  }

  @computed
  get temperatureQuery() {
    return {
      use_kmetrics: true,
      show_overlay: false,
      show_total_overlay: false,
      kmetrics: {
        measurement: '/components/temperature',
        dimensions: ['device_name'],
        metrics: [
          {
            name: 'instant',
            type: 'gauge'
          }
        ],
        range: {
          lookback: 'PT86400S'
        },
        merge: {
          dimensions: ['index'],
          aggregate: 'max'
        },
        window: {
          size: 0,
          fn: {
            instant: 'avg'
          }
        },
        rollups: {
          avg_instant: {
            metric: 'instant',
            aggregate: 'avg'
          },
          max_instant: {
            metric: 'instant',
            aggregate: 'max'
          },
          last_instant: {
            metric: 'instant',
            aggregate: 'last'
          }
        },
        sort: [
          {
            name: 'avg_instant',
            direction: 'desc'
          },
          {
            name: 'max_instant',
            direction: 'desc'
          },
          {
            name: 'last_instant',
            direction: 'desc'
          }
        ],
        limit: 5,
        includeTimeseries: 5,
        viz: {
          type: 'area',
          rollup: '',
          metric: 'instant'
        },
        filters: this.deviceFilter()
      }
    };
  }

  @computed
  get trafficQuery() {
    return {
      all_devices: false,
      device_name: [this.get('device_name')],
      lookback_seconds: 86400,
      aggregateTypes: ['avg_bits_per_sec'],
      show_overlay: false,
      show_total_overlay: false
    };
  }

  @computed
  get deviceClass() {
    const classification = this.get('class');
    const { isICMP, isICMPOnly, isNMS } = this;

    if (isICMPOnly) {
      return AllDeviceClass.Icmp;
    }

    if (isICMP) {
      return AllDeviceClass.FlowIcmp;
    }

    if (!isNMS) {
      return AllDeviceClass.Flow;
    }

    if (!classification) {
      // Show a default class for unclassified NMS devices
      return DEFAULT_DEVICE_CLASS;
    }

    if (!Object.values(AllDeviceClass).includes(classification)) {
      // If the device class is not one the UI knows how to display, return null
      return null;
    }

    return classification;
  }

  hasCapability(capability) {
    const { deviceClass } = this;
    if (!deviceClass) {
      return false;
    }
    return DeviceClassToCapabilities[deviceClass]?.includes(capability);
  }
}

export default DeviceModel;
